import React, { useCallback, useEffect, useRef, useState } from "react";
import "./selectWithSubOptions.scss";
import {
  getLabel,
  getValuesFromOptions,
  onSelectChange
} from "./SelectWithSubOptions.utils";
import { Option } from "../Select/Select";
import SingleCheckbox from "../SingleCheckbox/SingleCheckbox";

interface SelectWithSubOptions {
  selected: Option[];
  options: Option[];
  onChange: (e: any) => void;
}

const SelectWithSubOptions = ({
  options: allOptions,
  selected,
  onChange
}: SelectWithSubOptions) => {
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const ref = useRef<React.ElementRef<"div"> | null>(null);

  // Close select if user clicks elsewhere in the page
  const handleClickInPage = useCallback(
    (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        if (isOpen) setIsOpen(false);
      }
    },
    [isOpen, ref]
  );
  useEffect(() => {
    if (isOpen) {
      window.addEventListener("click", handleClickInPage);

      return () => {
        window.removeEventListener("click", handleClickInPage);
      };
    }
  }, [isOpen, handleClickInPage]);

  const mapOptions = (options, parentPaths: string[] = [], ml = 0) => (
    <>
      {options.map(option => {
        const optionPath = parentPaths.length
          ? [...parentPaths, option.value].join(".")
          : option.value;

        const optionIsAlreadySelected = getValuesFromOptions(selected).some(
          o => o === optionPath
        );

        return (
          <div className="fr-grid-row--gutters" key={optionPath}>
            <div>
              <SingleCheckbox
                className={`fr-ml-${ml * 8}v`}
                options={[
                  {
                    label: option.label,
                    nativeInputProps: {
                      name: optionPath,
                      checked: optionIsAlreadySelected,
                      onChange: () =>
                        onSelectChange(
                          option,
                          allOptions,
                          parentPaths,
                          optionPath,
                          selected,
                          onChange
                        )
                    }
                  }
                ]}
              />
            </div>

            {/* Option's potential sub-options */}
            {option.options &&
              optionIsAlreadySelected &&
              mapOptions(
                option.options,
                [...parentPaths, option.value],
                ml + 1
              )}
          </div>
        );
      })}
    </>
  );

  const handleKeyDown = e => {
    if (e.keyCode === 13) {
      setIsOpen(!isOpen);
    }
  };

  return (
    <div ref={ref} className="fr-mt-2v select-wrapper">
      <div
        role={"button"}
        tabIndex={0}
        className={`fr-select select ${isOpen ? "select-open" : ""}`}
        onClick={() => setIsOpen(!isOpen)}
        onKeyDown={handleKeyDown}
        aria-expanded={isOpen}
      >
        {getLabel(allOptions, getValuesFromOptions(selected))}
      </div>

      {isOpen && (
        <div className="dropDownWrapper">
          <div className="fr-container-fluid">{mapOptions(allOptions)}</div>
        </div>
      )}
    </div>
  );
};

export default SelectWithSubOptions;
